import { ref, computed, provide, inject, toRefs } from 'vue';

import * as math from './utils';

export type DateClasses = Record<string, string | string[]>;

export interface CalendarItem {
  id: string;
  startDate: Date;
  title?: string;
  tooltip?: string;
  endDate?: Date;
  url?: string;
  classes?: string[] | null;
  style?: string;
}

export interface NormalizedCalendarItem extends CalendarItem {
  endDate: Date;
  originalItem: CalendarItem;
  classes: string[];
  itemRow?: number;
}

export type DateTimeFormatOption = 'long' | 'short' | 'narrow' | undefined;

export interface PeriodRange {
  periodStart: Date;
  periodEnd: Date;
  displayFirstDate: Date;
  displayLastDate: Date;
}

export type PeriodChangedCallback = (
  periodRange: PeriodRange,
  source: 'watch' | 'user',
) => void;

export interface CalendarProps {
  showDate?: Date;
  locale?: string;
  selectionStart?: Date;
  selectionEnd?: Date;
  periodChangedCallback?: PeriodChangedCallback;
  displayPeriodUom: string;
  displayPeriodCount: number;
  monthNameFormat: DateTimeFormatOption;
  weekdayNameFormat: DateTimeFormatOption;
  startingDayOfWeek: number;
  items: CalendarItem[];
  dateClasses: DateClasses;
  currentPeriodLabel: string;
  currentPeriodLabelIcons: string;
  monthNameOn1st: boolean;
  displayWeekNumbers: boolean;
  showTimes: boolean;
  timeFormatOptions: object;
  disablePast: boolean;
  disableFuture: boolean;
  enableDateSelection: boolean;
  enableDragDrop: boolean;
  enableTooltip: boolean;
  enableHtmlTitles: boolean;
  itemTop: string;
  itemContentHeight: string;
  itemBorderHeight: string;
  doEmitItemMouseEvents: boolean;
}

/**
 * Creates a calendar state object.
 *
 * @param props Calendar state props.
 * @returns Calendar state object.
 */
export function createCalendarState(props: CalendarProps) {
  const currentDragItem = ref<NormalizedCalendarItem | null>(null);
  const dateSelectionOrigin = ref<Date | null>(null);
  const currentHoveredItemId = ref('');

  // Props cannot default to computed/method returns, so create defaulted
  // version of this property and use it rather than the bare prop (Vue Issue
  // #6013).
  const displayLocale = computed(() => {
    return props.locale ?? math.getDefaultBrowserLocale();
  });

  // ShowDate, but defaulted to today. Needed both for periodStart below and for
  // the "outside of month" class. Any time component passed as part of showDate
  // is discarded.
  const defaultedShowDate = computed(() => {
    return props.showDate ? math.dateOnly(props.showDate) : math.today();
  });

  // Given the showDate, defaulted to today, computes the beginning and end of
  // the period that the date falls within.
  const periodStart = computed(() => {
    return math.beginningOfPeriod(
      defaultedShowDate.value,
      props.displayPeriodUom,
      props.startingDayOfWeek,
    );
  });

  const periodEnd = computed(() => {
    return math.addDays(
      math.incrementPeriod(
        periodStart.value,
        props.displayPeriodUom,
        props.displayPeriodCount,
      ),
      -1,
    );
  });

  const periodStartCalendarWeek = computed(() => {
    const jan1 = new Date(periodStart.value.getFullYear(), 0, 1);
    const firstThursday = math.addDays(jan1, (11 - jan1.getDay()) % 7);

    const startOfFirstWeek = math.beginningOfPeriod(
      firstThursday,
      'week',
      props.startingDayOfWeek,
    );

    const periodWeekStarts = math.beginningOfWeek(
      periodStart.value,
      props.startingDayOfWeek,
    );

    return 1 + Math.floor(math.dayDiff(startOfFirstWeek, periodWeekStarts) / 7);
  });

  // For month and year views, the first and last dates displayed in the grid
  // may not be the same as the intended period, since the period may not start
  // and stop evenly on the starting day of the week.
  const displayFirstDate = computed(() => {
    return math.beginningOfWeek(periodStart.value, props.startingDayOfWeek);
  });

  const displayLastDate = computed(() => {
    return math.endOfWeek(periodEnd.value, props.startingDayOfWeek);
  });

  // Create an array of dates, where each date represents the beginning of a
  // week that should be rendered in the view for the current period.
  const weeksOfPeriod = computed(() => {
    const numWeeks = Math.floor(
      (math.dayDiff(displayFirstDate.value, displayLastDate.value) + 1) / 7,
    );

    return [...Array(numWeeks)].map((_, i) =>
      math.addDays(displayFirstDate.value, i * 7),
    );
  });

  // Cache the names based on current locale and format settings
  const monthNames = computed(() => {
    return math.getFormattedMonthNames(
      displayLocale.value,
      props.monthNameFormat,
    );
  });

  const weekdayNames = computed(() => {
    return math.getFormattedWeekdayNames(
      displayLocale.value,
      props.weekdayNameFormat,
      props.startingDayOfWeek,
    );
  });

  // Ensure all item properties have suitable default
  const fixedItems = computed(() => {
    if (!props.items) return [];

    return props.items.map((item) =>
      math.normalizeItem(item, item.id === currentHoveredItemId.value),
    );
  });

  // Period that today's date sits within
  const currentPeriodStart = computed(() => {
    return math.beginningOfPeriod(
      math.today(),
      props.displayPeriodUom,
      props.startingDayOfWeek,
    );
  });

  const currentPeriodEnd = computed(() => {
    return math.addDays(
      math.incrementPeriod(
        currentPeriodStart.value,
        props.displayPeriodUom,
        props.displayPeriodCount,
      ),
      -1,
    );
  });

  // Creates the HTML to render the date range for the calendar header.
  const periodLabel = computed(() => {
    return math.formattedPeriod(
      periodStart.value,
      periodEnd.value,
      props.displayPeriodUom,
      monthNames.value,
    );
  });

  const currentPeriodLabelFinal = computed(() => {
    const c = currentPeriodStart.value;
    const s = periodStart.value;

    if (!props.currentPeriodLabel)
      return math.formattedPeriod(
        c,
        currentPeriodEnd.value,
        props.displayPeriodUom,
        monthNames.value,
      );

    if (props.currentPeriodLabel === 'icons') {
      return props.currentPeriodLabelIcons[
        Math.sign(c.getTime() - s.getTime()) + 1
      ];
    }

    return props.currentPeriodLabel;
  });

  const showMonthNameOn1st = computed(() => {
    return (
      props.monthNameOn1st &&
      (props.displayPeriodUom !== 'month' || props.displayPeriodCount > 1)
    );
  });

  const calendar = {
    ...toRefs(props),
    currentDragItem,
    dateSelectionOrigin,
    currentHoveredItemId,
    displayLocale,
    defaultedShowDate,
    periodStart,
    periodEnd,
    periodStartCalendarWeek,
    displayFirstDate,
    displayLastDate,
    weeksOfPeriod,
    monthNames,
    weekdayNames,
    fixedItems,
    currentPeriodStart,
    currentPeriodEnd,
    periodLabel,
    currentPeriodLabelFinal,
    showMonthNameOn1st,
  };

  provide('calendarState', calendar);

  return calendar;
}

export type CalendarState = ReturnType<typeof createCalendarState>;

/**
 * Inject locally-provided {@link CalendarState} data into current component.
 */
export function injectCalendarState() {
  return inject('calendarState') as CalendarState;
}
